import axios from "axios";
import "./ForgetPassword.css";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { ThreeDots } from "react-loader-spinner";
import Tick from "../../images/icons/green-tick.svg";
import LoginImage from "../../images/login/lg-img.png";
import EyeIcon from "../../images/icons/eye-icon.svg";
import EyeCrossed from "../../images/icons/eye-crossed.svg";
import { login, loginTemp } from "../../features/auth/authSlice";
import React, { useState, useRef, useEffect } from "react";
import LoginImageMobile from "../../images/login/lg-img-mob.png";
import { setForex } from "../../features/forex/forexSlice.js";
import { setDashboardCurrencyRx } from "../../features/invoice/invoiceSlice";
import { setAppSettings } from "../../features/appSettings/appSettingsSlice.js";
import userTypeConsts from "../../constants/userTypeConsts.js";
import { getBrandLogo, getBrandLogoAlt } from "../../utils/brandLogo";


function ResetPass() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { email, token } = useParams();
  const [decodedEmail, setDecodedEmail] = useState("");
  const [passwordMatchError, setPasswordMatchError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordEmptyError, setPasswordEmptyError] = useState(false);
  const [passwordConfirmationEmptyError, setPasswordConfirmationEmptyError] = useState(false);
  const passwordRef = useRef(null);
  const passwordConfirmationRef = useRef(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const abortControllerRef = useRef(null);


  useEffect(() => {
    // Decode email from URL params with error handling
    let decoded = "";
    try {
      if (email) {
        decoded = decodeURIComponent(
          atob(email.replace(/-/g, "+").replace(/_/g, "/"))
        );
        setDecodedEmail(decoded);
      }
    } catch (decodeError) {
      console.error("Failed to decode email from URL:", decodeError);
      toast.error("Invalid reset link format. Please request a new password reset.", {
        position: "bottom-right",
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
      });
      navigate("/forget-password");
      return;
    }

    const verifyResetPassLink = async () => {
      // Create AbortController for this request
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      try {
        const currentTime = new Date().toISOString();

        if (!decoded || !token) {
          toast.error("Invalid or missing reset link.", {
            position: "bottom-right",
            autoClose: 2000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "light",
          });
          navigate("/forget-password");
          return;
        }

        const response = await axios.post(
          "/api/user/check/reset-password/validity",
          {
            email: decoded,
            token,
            currentTime,
            statusCheck: false,
          },
          { signal }
        );

        if (response.status === 204) {
          toast.error("Sorry! Reset link has expired.", {
            position: "bottom-right",
            autoClose: 2000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "light",
          });
          navigate("/forget-password");
        }
      } catch (error) {
        // Don't show error if request was aborted
        if (axios.isCancel(error) || error.name === 'AbortError') {
          return;
        }
        console.error("Reset password link verification failed:", error);
        global?.config?.slackMessage?.(`Reset link verification error: ${error.toString()}`);
        toast.error(
          error?.response?.data?.message || "Something went wrong while verifying the reset link. Please try again.",
          {
            position: "bottom-right",
            autoClose: 2000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "light",
          }
        );
        navigate("/forget-password");
      }
    };

    if (decoded) {
      verifyResetPassLink();
    }

    // Cleanup: abort request on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [email, navigate, token]);

  const fetchForex = async () => {
    try {
      const response = await axios.post(`/api/forex/get`, { base_currency: "USD" });

      const forexData = response.data?.json;

      if (!forexData) {
        console.warn("No forex data received from API.");
        return null;
      }
      await dispatch(setForex(forexData));
      return forexData;

    } catch (error) {
      console.error("Failed to fetch forex data:", error);

      // Optional: Show toast if needed
      toast?.error("Unable to fetch forex rates. Please try again later.", {
        position: "bottom-right",
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
      });

      return null; // Fallback: prevent crashes if data is used elsewhere
    }
  };


  const onSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setPasswordError("");
    setPasswordMatchError("");
    setPasswordEmptyError(false);
    setPasswordConfirmationEmptyError(false);

    let hasErrors = false;

    // Validate empty fields
    if (!password || password.trim() === "") {
      setPasswordEmptyError(true);
      setPasswordError("Password is required");
      passwordRef.current?.focus();
      hasErrors = true;
    }

    if (!passwordConfirmation || passwordConfirmation.trim() === "") {
      setPasswordConfirmationEmptyError(true);
      setPasswordMatchError("Please confirm your password");
      if (!hasErrors) {
        passwordConfirmationRef.current?.focus();
      }
      hasErrors = true;
    }

    if (hasErrors) {
      return;
    }

    // Validate password match
    if (password !== passwordConfirmation) {
      setPasswordMatchError("Passwords do not match");
      setPasswordConfirmationEmptyError(true);
      passwordConfirmationRef.current?.focus();
      return;
    }

    // Fixed password regex (removed corrupted math text)
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-.[\]{};':"\\|,.<>/?])[A-Za-z\d!@#$%^&*()_+\-.[\]{};':"\\|,.<>/?]{8,}$/;

    if (!passwordRegex.test(password)) {
      setPasswordError("Invalid password format");
      setPasswordEmptyError(true);
      toast.error(
        "Password must be at least 8 characters long and include at least one uppercase letter, one lowercase letter, one digit, and one special character.",
        { position: "bottom-right", autoClose: 2500 }
      );
      passwordRef.current?.focus();
      return;
    }

    setLoading(true);

    // Create AbortController for reset password requests
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {

      const requestVerifyData = {
        email: decodedEmail,
        token,
        currentTime: new Date().toISOString(),
        statusCheck: true,
      };

      const verifyResponse = await axios.post(
        "/api/user/check/reset-password/validity",
        requestVerifyData,
        { signal }
      );

      if (verifyResponse.status === 204) {
        toast.error("Reset link expired", { position: "bottom-right", autoClose: 2500 });
        navigate("/forget-password");
        setLoading(false);
        return;
      }

      const resetResponse = await axios.post(
        "/api/user/reset-password",
        {
          email: decodedEmail,
          password,
        },
        { signal }
      );

      if (resetResponse.data.status !== "success") {
        toast.error(
          resetResponse.data.message || "Something went wrong while resetting your password.",
          {
            position: "bottom-right",
            autoClose: 2500,
          }
        );
        global?.config?.activityLog?.(
          window.location.href,
          "Reset Password",
          "Reset password failed"
        );
        setLoading(false);
        return;
      }

      toast.success("Your password has been reset successfully", {
        position: "bottom-right",
        autoClose: 1500,
      });

      setShowSuccessMessage(true);

      setTimeout(async () => {
        // Create new AbortController for login flow
        const loginAbortController = new AbortController();
        abortControllerRef.current = loginAbortController;

        try {
          const loginResponse = await fetch("/api/user/login", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: decodedEmail, password }),
            signal: loginAbortController.signal,
          });

          const loginData = await loginResponse.json();

          if (!loginData?.token) {
            toast.error(loginData?.message || "Login failed after password reset", {
              position: "bottom-right",
              autoClose: 2000,
            });
            return;
          }

          await dispatch(loginTemp(loginData));
          await fetchForex();

          const tokenHeader = {
            headers: { Authorization: `Bearer ${loginData.token}` },
          };

          const [authSettingsBundle] = await Promise.all([
            axios.get(`/api/auth/config/bundle/${loginData.appId}/${loginData._id}`, tokenHeader),
          ]);

          const userSettings = authSettingsBundle.data.userSettings || {};
          const favIconData = authSettingsBundle.data.ownerDetails || {};
          const settingsData = authSettingsBundle.data.appSettings || {};

          const favIcon_fileName = favIconData?.fav_icon || "fav_icon_default.jpeg";
          const app_owner_name = favIconData?.commercial_name || "TalenTon";
          const app_owner_id = favIconData?._id || "";

          if (settingsData && loginData.userType !== userTypeConsts.SUPER_ADMIN && loginData.userType !== userTypeConsts.ADMIN) {
            settingsData.timeFormat = userSettings?.timeFormat ?? 12;
          }

          if (settingsData) {
            // Preserve the tenant timezone before `timeZone` is replaced with
            // the user's own — see the same note in Login.jsx.
            settingsData.appTimeZone = settingsData.timeZone || null;

            settingsData.currency = userSettings?.currency || "USD";
            settingsData.timeZone = userSettings?.timeZone || null;
          }

          await dispatch(setAppSettings({
            ...settingsData,
            favIcon: favIcon_fileName,
            app_ownername: app_owner_name,
            app_ownerid: app_owner_id,
          }));

          // Set Dashboard Currency
          try {
             const currencyResponse = authSettingsBundle.data.invoiceCurrencies;
            if (currencyResponse.length > 0) {
              localStorage.setItem('DashboardCurrency', JSON.stringify(currencyResponse[0]));
              dispatch(setDashboardCurrencyRx(currencyResponse[0]));
            }
          } catch (currencyErr) {
            console.warn("Failed to fetch dashboard currency:", currencyErr);
          }

          // 2FA Handling
          let twoFactorEnabled = settingsData?.twoFa === true;

          const stored2FA = {
            enabled: localStorage.getItem('twoFactorEnabled'),
            at: localStorage.getItem('twoFactorEnabledAt')
          };

          if (twoFactorEnabled && stored2FA.enabled && stored2FA.at && Date.now() - parseInt(stored2FA.at, 10) < 30 * 24 * 60 * 60 * 1000) {
            dispatch(login(loginData));
            navigate("/user/dashboard");
          } else if (userSettings?.twoFa === false) {
            dispatch(login(loginData));
            navigate("/user/dashboard");
          } else {
            navigate("/2fa");
          }

        } catch (loginErr) {
          // Don't show error if request was aborted
          if (loginErr.name === 'AbortError') {
            return;
          }
          console.error("Error during post-reset login flow:", loginErr);
          global?.config?.slackMessage?.("Post-reset login error: " + loginErr.toString());
          toast.error(
            loginErr?.response?.data?.message || "An error occurred after resetting your password. Please try again.",
            {
              position: "bottom-right",
              autoClose: 2000,
            }
          );
        }
      }, 1000);

    } catch (err) {
      // Don't show error if request was aborted
      if (axios.isCancel(err) || err.name === 'AbortError') {
        return;
      }
      console.error("Reset password error:", err);
      global?.config?.slackMessage?.("Reset password flow error: " + err.toString());
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred. Please try again.",
        {
          position: "bottom-right",
          autoClose: 1500,
        }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="login-outer-forget">
        <div className="login-inner">
          <div className="top-logo-mobile">
            <img src={getBrandLogo()} alt={getBrandLogoAlt()} />
          </div>
          <div className="lg-lft">
            <img src={LoginImage} alt="desktop" className="desktop-lg-img" fetchpriority="high" />
            <img
              src={LoginImageMobile}
              alt="mobile"
              className="mobile-lg-img"
            />
          </div>
          <div className="lg-rgt">
            <div className="top-logo">
              <img src={getBrandLogo()} alt={getBrandLogoAlt()} />
            </div>
            <div className="login-form-sec">
              {showSuccessMessage ? (
                <div className="success-message-reset">
                  <div className="tick-icon-reset">
                    <img src={Tick} alt="Tick" />
                  </div>
                  <div className="success-for">
                    <h3>Password changed!</h3>
                    <p>Your password has been changed successfully.</p>
                  </div>

                </div>
              ) : (
                <form onSubmit={onSubmit}>
                  <h3 className="reset-title">Create a new password</h3>
                  <input
                    type="hidden"
                    placeholder="email"
                    value={decodedEmail}
                    readOnly
                  />
                  <div className="form-outer">
                    <label>Password *</label>
                    <div className="pass-eye">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        id="password"
                        placeholder="Password"
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          // Clear errors when user starts typing
                          if (passwordEmptyError || passwordError) {
                            setPasswordEmptyError(false);
                            setPasswordError("");
                          }
                        }}
                        onBlur={() => {
                          // Validate on blur
                          if (!password || password.trim() === "") {
                            setPasswordEmptyError(true);
                            setPasswordError("Password is required");
                          }
                        }}
                        ref={passwordRef}
                        required
                        className={passwordEmptyError || passwordError ? "error" : ""}
                        style={{
                          borderColor: passwordEmptyError || passwordError ? "#ef4444" : "",
                          borderWidth: passwordEmptyError || passwordError ? "2px" : "1px"
                        }}
                      />
                      <img
                        src={showPassword ? EyeCrossed : EyeIcon}
                        className="password-toggle-icon"
                        alt="Toggle Password"
                        onClick={() => setShowPassword(!showPassword)}
                      />
                    </div>
                    {(passwordError || passwordEmptyError) && (
                      <span className="text-red-500 text-xs italic" style={{ marginTop: "4px", display: "block" }}>
                        {passwordError || "Password is required"}
                      </span>
                    )}
                  </div>
                  <div className="form-outer">
                    <label>
                      Confirm Password *
                    </label>
                    <input
                      type="password"
                      placeholder="Confirm Password"
                      value={passwordConfirmation}
                      onChange={(e) => {
                        setPasswordConfirmation(e.target.value);
                        // Clear errors when user starts typing
                        if (passwordConfirmationEmptyError || passwordMatchError) {
                          setPasswordConfirmationEmptyError(false);
                          setPasswordMatchError("");
                        }
                      }}
                      onBlur={() => {
                        // Validate on blur
                        if (!passwordConfirmation || passwordConfirmation.trim() === "") {
                          setPasswordConfirmationEmptyError(true);
                          setPasswordMatchError("Please confirm your password");
                        } else if (password && password !== passwordConfirmation) {
                          setPasswordMatchError("Passwords do not match");
                          setPasswordConfirmationEmptyError(true);
                        }
                      }}
                      ref={passwordConfirmationRef}
                      required
                      className={passwordConfirmationEmptyError || passwordMatchError ? "error" : ""}
                      style={{
                        borderColor: passwordConfirmationEmptyError || passwordMatchError ? "#ef4444" : "",
                        borderWidth: passwordConfirmationEmptyError || passwordMatchError ? "2px" : "1px"
                      }}
                    />
                    {(passwordMatchError || passwordConfirmationEmptyError) && (
                      <span className="text-red-500 text-xs italic" style={{ marginTop: "4px", display: "block" }}>
                        {passwordMatchError || "Please confirm your password"}
                      </span>
                    )}
                  </div>
                  <div className="text-center mt-6">
                    <button
                      className="reset-pass"
                      type="submit"
                    >
                      {loading ?
                        <div className="outter-load-table">
                          <ThreeDots
                            height="18"
                            width="20"
                            radius="9"
                            color="white"
                            ariaLabel="three-dots-loading"
                            wrapperStyle={{}}
                            wrapperClassName=""
                            visible={true}
                          />
                        </div> :
                        "Reset Password"
                      }
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

    </>
  );
}

export default ResetPass;